/* eslint-disable max-len */
import { VoidCallback } from '@deqo/foundation';
import React, { useCallback } from 'react';
import { Icon } from '../../../concepts/resources/Icon';
import { LabelValue } from '../../../platform/types/LabelValue';
import { Cell } from '../../../ui';
import { ButtonToggle } from '../../../ui/ButtonToggle';
import { DropButton } from '../../../ui/DropButton';
import { Slider } from '../../../ui/Slider';
import { Flexer, Label } from '../../dashboard/elements/LayoutElements';
import { FlexLayoutConfig } from './FlexLayout';

export const FlexLayoutConfigLabel = ({ label }: { label: string }) =>
    <Flexer flex="row" align="center" justify="end" padding="0 .5 0 0">
        <Label>{label}:</Label>
    </Flexer>;

export const FlexLayoutConfigButton = ({ icon, active, disabled, onClick }: { icon: string, active?: boolean, disabled?: boolean, onClick?: VoidCallback }) =>
    <DropButton overflow="0" padding=".125" active={active} disabled={disabled} onClick={onClick}>
        <Icon size="1.75" src={icon} />
    </DropButton>;

const layoutOpts = [
    LabelValue('Row', 'row'),
    LabelValue('Column', 'column'),
    LabelValue('None', 'none'),
];

export const FlexLayoutConfigWidget = ({ value, onChange }: { value: FlexLayoutConfig, onChange: (newValue: FlexLayoutConfig) => void }) => {
    value = FlexLayoutConfig.from(value);

    const onSelect = useCallback((patch: Record<number, string>) => {
        const newValue = FlexLayoutConfig.from(Object.values({ ...value, ...patch }));
        onChange(newValue);
    }, [value]);

    const onChangeGap = useCallback((gap: number) => {
        const next = [...value];
        next[3] = gap * 10;
        onChange(FlexLayoutConfig.from(next));
    }, [value]);

    const disabled = value[0] === 'none';

    return <>
        <FlexLayoutConfigLabel label="Stack" />
        <Flexer flex style={{ gridColumnEnd: 'span 3' }}>
            <ButtonToggle size="md" options={layoutOpts} selected={value[0]} onChange={x => onSelect({ 0: x })} />
        </Flexer>
        {value[0] === 'row' && <>
            <FlexLayoutConfigLabel label="Justify" />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-justify-start" {...{ disabled }} active={value[2] === 'start'} onClick={() => onSelect({ 2: 'start' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-justify-center" {...{ disabled }} active={value[2] === 'center'} onClick={() => onSelect({ 2: 'center' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-justify-end" {...{ disabled }} active={value[2] === 'end'} onClick={() => onSelect({ 2: 'end' })} />
            <FlexLayoutConfigLabel label="Align" />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-align-start" {...{ disabled }} active={value[1] === 'start'} onClick={() => onSelect({ 1: 'start' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-align-center" {...{ disabled }} active={value[1] === 'center'} onClick={() => onSelect({ 1: 'center' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-align-end" {...{ disabled }} active={value[1] === 'end'} onClick={() => onSelect({ 1: 'end' })} />
        </>}
        {value[0] !== 'row' && <>
            <FlexLayoutConfigLabel label="Justify" />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-justify-start" {...{ disabled }} active={value[1] === 'start'} onClick={() => onSelect({ 1: 'start' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-justify-center" {...{ disabled }} active={value[1] === 'center'} onClick={() => onSelect({ 1: 'center' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-justify-end" {...{ disabled }} active={value[1] === 'end'} onClick={() => onSelect({ 1: 'end' })} />
            <FlexLayoutConfigLabel label="Align" />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-align-start" {...{ disabled }} active={value[2] === 'start'} onClick={() => onSelect({ 2: 'start' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-align-center" {...{ disabled }} active={value[2] === 'center'} onClick={() => onSelect({ 2: 'center' })} />
            <FlexLayoutConfigButton icon="deqo://icons/d2/flex-align-end" {...{ disabled }} active={value[2] === 'end'} onClick={() => onSelect({ 2: 'end' })} />
        </>}
        <FlexLayoutConfigLabel label="Gap" />
        <Cell width={3}>
            <Flexer flex="row" align="center" relative height="100%">
                <Flexer grow>
                    <Slider {...{ disabled }} min={0} max={25} value={(value[3] || 0) / 10} onChange={onChangeGap} />
                </Flexer>
            </Flexer>
        </Cell>
    </>;
};
